import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { ModerationItem } from '@/api/pending'
import { queryKeys } from '@/api/queryKeys'
import { useToast } from '@/ui'

export type ReviewAction = 'approve' | 'reject' | 'remove' | 'keep'

interface Vars {
  action: ReviewAction
  item: ModerationItem
}

const COLLECTION = (kind: ModerationItem['kind']) => (kind === 'post' ? 'posts' : 'polls')

function request(condoId: number, { action, item }: Vars) {
  const base = `/condominiums/:id/${COLLECTION(item.kind)}/:itemId`
  const params = { id: condoId, itemId: item.id }
  switch (action) {
    case 'approve':
      return api.patch(`${base}/approve`, undefined, { params })
    case 'reject':
      // Posts: PATCH …/reject {reason?} (API-2). Polls are cancelled instead.
      return item.kind === 'post'
        ? api.patch(`${base}/reject`, {}, { params })
        : api.patch(`${base}/cancel`, undefined, { params })
    case 'remove':
      return api.patch(`${base}/moderate`, { action: 'delete' }, { params })
    case 'keep':
      return api.patch(`${base}/moderate`, { action: 'dismiss' }, { params })
  }
}

const listKey = (condoId: number, { action, item }: Vars) => {
  const reported = action === 'remove' || action === 'keep'
  const group = item.kind === 'post' ? queryKeys.posts : queryKeys.polls
  return reported ? group.reported(condoId) : group.pending(condoId)
}

const SUCCESS: Record<ReviewAction, (title: string) => string> = {
  approve: (t) => `“${t}” está no feed`,
  reject: (t) => `“${t}” foi recusado`,
  remove: () => 'Conteúdo removido e autor avisado',
  keep: () => 'Denúncias descartadas, conteúdo continua no ar',
}

/**
 * Approve / reject / remove / keep, optimistic: the card leaves the list at once, and comes back
 * with an error toast if the API refuses. Shared by Aprovações and the Visão geral preview.
 */
export function useReviewAction(condoId: number) {
  const qc = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: (vars: Vars) => request(condoId, vars),
    onMutate: async (vars) => {
      const key = listKey(condoId, vars)
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<ModerationItem[]>(key)
      qc.setQueryData<ModerationItem[]>(key, (list) => list?.filter((x) => x.id !== vars.item.id))
      return { key, previous }
    },
    onSuccess: (_data, vars) => toast(SUCCESS[vars.action](vars.item.title)),
    onError: (_err, _vars, ctx) => {
      if (ctx) qc.setQueryData(ctx.key, ctx.previous)
      toast('Não foi possível concluir a ação. Tente novamente.', { icon: 'error' })
    },
    onSettled: (_data, _err, vars) => qc.invalidateQueries({ queryKey: listKey(condoId, vars) }),
  })
}
